import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {AnimatePresence, motion} from 'framer-motion';
import {ContactCTA, Footer, LazyImage, Navbar, NigeriaMap, ProjectCard} from '../../components';
import {PROJECTS} from '../../data/projects';
import styles from './Landing.module.css';

// ─── Image assets ─────────────────────────────────────────────────────────────
// Cloudinary transforms: f_auto = WebP/AVIF, q_auto = smart quality,
// w_N = cap pixel width to max rendered size × 2 (retina)
const CL = 'https://res.cloudinary.com/dhjzovpof/image/upload';
const IMAGES = {
    heroBg: `${CL}/w_1160,h_870,c_fill,f_auto,q_auto/v1773592072/DJI_0679_2_fuymi4.jpg`,
    // Mission/vision cards — 580×435 desktop / 651×488 mobile (2× retina each)
    missionImg: `${CL}/w_1160,h_870,c_fill,f_auto,q_auto/v1772401597/3fa02a52-7d11-46f0-a38c-3905f3842376_wx9m79.jpg`,
    missionImgMd: `${CL}/w_400,h_220,c_fill,f_auto,q_auto/v1772401597/3fa02a52-7d11-46f0-a38c-3905f3842376_wx9m79.jpg`,
    visionImg: `${CL}/w_1160,h_870,c_fill,f_auto,q_auto/v1772399285/b8b81c2a-e4ef-452a-b067-3f2286a5b1b5_1_btdqsi.jpg`,
    visionImgMd: `${CL}/w_400,h_220,c_fill,f_auto,q_auto/v1772399285/b8b81c2a-e4ef-452a-b067-3f2286a5b1b5_1_btdqsi.jpg`,
    // Video thumbnail — full content width, ~1400px cap
    videoImg: `${CL}/w_1400,f_auto,q_auto/v1772401462/b50f623c-041a-4ad4-a164-4b61d6c2280f_ltmods.jpg`,
    // Map background PNG — ~900px display
    mapBg: `${CL}/w_900,f_auto,q_auto/v1772401049/8305fb16-60dd-410e-8afb-92ffe2ee6b0f_axpsay.png`,
    // Service card images — ~50% content width, ~700px CSS → 1400px cap
    service1: `${CL}/w_1400,h_870,c_fill,f_auto,q_auto/v1772399359/785dadcf-7da9-4b2b-bd05-5b437e649d9a_1_kuyxh0.jpg`,
    service2: `${CL}/w_1400,h_870,c_fill,f_auto,q_auto/v1775287464/ff45da63c9621f258d7f49ee9f9da58e072c26be_1_me7o9b.jpg`,
    // service3: `${CL}/w_1400,h_870,c_fill,f_auto,q_auto/v1775288268/Img_2_n4gcty.png`,
    service3: `${CL}/w_1400,h_870,c_fill,f_auto,q_auto/v1780562572/WhatsApp_Image_2026-06-04_at_7.26.34_AM_dubjkx.jpg`,
    arrowRight: 'https://res.cloudinary.com/dhjzovpof/image/upload/v1772401703/b91660fa-4f04-40d7-927f-d1b098aa7a59_1_uzsrtg.svg',
    playIcon: 'https://res.cloudinary.com/dhjzovpof/image/upload/v1772401733/c08c2c10-8d7b-4c01-a3d0-10e336288ecf_1_wcvtv1.svg',
    sunIcon: 'https://res.cloudinary.com/dhjzovpof/image/upload/v1772401765/0c9c6c19-794a-4f99-aa67-91a08fd640e8_1_l1rb48.svg',
    roadIcon: 'https://res.cloudinary.com/dhjzovpof/image/upload/v1772401163/7436d7b7-d987-4c50-ac25-35d9b3401bfc_1_kvodid.svg',
    shieldIcon: 'https://res.cloudinary.com/dhjzovpof/image/upload/v1773596816/Shield_f2n52b.svg',
    logoWatermark: 'https://res.cloudinary.com/dhjzovpof/image/upload/v1773094633/dbae1ac767fb73178c9e75eafff0eb62f069bf78_layuwm.png',

    serviceIcon1: 'https://res.cloudinary.com/dhjzovpof/image/upload/v1773596902/9ca917f9-4b19-4a72-9d02-6e537f18d42d_wuyyu7.png',
    serviceIcon2: 'https://res.cloudinary.com/dhjzovpof/image/upload/v1773596931/0de01662-6f40-48de-a3b5-c4b327ef7d7a_zbuos8.png',
    serviceIcon3: 'https://res.cloudinary.com/dhjzovpof/image/upload/v1773596969/235f3e91-a91d-4572-be7c-1b0473ecae31_wbi86b.png',
};

const PARTNERS = [
    'https://res.cloudinary.com/dhjzovpof/image/upload/v1773595550/9c2004835becd41e461f5e5bb29ff01d23f7ddda_e9qlaw.jpg',
    'https://res.cloudinary.com/dhjzovpof/image/upload/v1773595744/9cd772129b7e29be6977d9d598249eb23d0bfb9e_mjwgit.png',
    'https://res.cloudinary.com/dhjzovpof/image/upload/v1773595798/ee97329ab3e4210fb3cba3c6f4e051d8785acc41_yv0xoh.png',
    'https://res.cloudinary.com/dhjzovpof/image/upload/v1773595837/15fca2915b640c7365bb8c2fbdefffaad5253c81_wuzyda.png',
    'https://res.cloudinary.com/dhjzovpof/image/upload/v1773595883/8db23edab07c10713d51a29dbe11a7867012206e_hcncgj.png',
    'https://res.cloudinary.com/dhjzovpof/image/upload/v1773595946/b6a21cf5b8da6e5a625df26b4cb2ce22ae33a547_aiih7d.png',
    'https://res.cloudinary.com/dhjzovpof/image/upload/v1773596347/59b96960693b33f7199681ef66432a1a5c5e1338_t2g6dp.png',
    'https://res.cloudinary.com/dhjzovpof/image/upload/v1773596402/b491636cee9b683bf402430b21a1d5725d7df421_ieeamo.jpg',
    'https://res.cloudinary.com/dhjzovpof/image/upload/v1773596454/3bfc32097bef7d35ee066b0a21f1edeb1570e3fc_o0dac4.jpg',
    'https://res.cloudinary.com/dhjzovpof/image/upload/v1773596492/76fb165b7f38d60046af371794d62b327ef9830a_yjdwmd.png',
    'https://res.cloudinary.com/dhjzovpof/image/upload/v1773596526/c53ba099e880aaf1511e45aa747054b747a7e12a_nxckmi.jpg',
    'https://res.cloudinary.com/dhjzovpof/image/upload/v1773596560/d487c23b3ca496ff8d8dbd73b0e3d2fa70dfeaa4_kbnozn.png',
    'https://res.cloudinary.com/dhjzovpof/image/upload/v1773596591/00dcdb348ac8aa2f659387c1818b1cbd35651489_invtgq.png',
    'https://res.cloudinary.com/dhjzovpof/image/upload/v1773596636/277e699cf9cfa368b5a24ea187689853ca35b70e_ntasrr.png',
    'https://res.cloudinary.com/dhjzovpof/image/upload/v1773596694/33094e87-7f18-4a9e-aacc-db07a84a3f43_nqet8t.jpg',
];

const HERO_SLIDES = [
    {
        image: "https://res.cloudinary.com/dhjzovpof/image/upload/v1773595416/b51ce9f15e93266502bc3bf7c2413ca7a15917b5_1_ivmfuu.jpg",

         taglinePrefix: 'Welcome to ',
        taglineBrand: 'TEKTRON',
        taglineSuffix: '+',
        title: 'Building a Sustainable Future Today.',
        desc: "Integrating advanced road construction and road furniture with sustainable renewable energy. We are engineering a smarter, more resilient world through efficiency and clean energy.",
    },
    {
        image: "https://res.cloudinary.com/dhjzovpof/image/upload/v1779993760/IMG_4226_3_1_xoi8um.jpg",
    },
    {
        image: "https://res.cloudinary.com/dhjzovpof/image/upload/v1773592072/DJI_0679_2_fuymi4.jpg",
    },
    {
        image: "https://res.cloudinary.com/dhjzovpof/image/upload/v1773594125/DJI_0148_1_bnaeyb.jpg",
        taglinePrefix: 'Trusted Across ',
        taglineBrand: 'NIGERIA',
        taglineSuffix: '',
        title: 'Connecting Communities Across The Nation.',
        desc: "With over 40 completed projects across 20+ states, our footprint spans the full breadth of Nigeria's infrastructure needs — from highways to smart traffic systems.",
    },
    {
        image: "https://res.cloudinary.com/dhjzovpof/image/upload/v1773594578/86640ab90ff0b803f1c893437ce23e27e6a79702_5_qvy3ei.jpg",
        taglinePrefix: 'Trusted Across ',
        taglineBrand: 'NIGERIA',
        taglineSuffix: '',
        title: 'Connecting Communities Across The Nation.',
        desc: "With over 40 completed projects across 20+ states, our footprint spans the full breadth of Nigeria's infrastructure needs — from highways to smart traffic systems.",
    },
    {
        "image": "https://res.cloudinary.com/dhjzovpof/image/upload/ar_16:9,c_auto/IMG_6777_1_nrnkwg.jpg",
    },
    {
        "image": "https://res.cloudinary.com/dhjzovpof/image/upload/v1779993210/IMG_2808_2_1_e9ruxt.jpg"
    },

    {
        "image": "https://res.cloudinary.com/dhjzovpof/image/upload/v1779992666/tektron/asset_ap_owor_3_1779992661391_001.png"
    },
    {
        image: "https://res.cloudinary.com/dhjzovpof/image/upload/v1776713848/ChatGPT_Image_Apr_20_2026_08_36_12_PM_hsmacq.png",
        taglinePrefix: 'Excellence in ',
        taglineBrand: 'ENGINEERING',
        taglineSuffix: '',
        title: 'Infrastructure That Stands The Test Of Time.',
        desc: "From thermoplastic road marking to solar hybrid installations, we deliver precision-engineered solutions that transform Nigeria's transport network.",
    },
];

const SLIDE_DURATION = 5000;


const slideVariants = {
    enter: {opacity: 0},
    center: {opacity: 1},
    exit: {opacity: 0},
};


const fadeUp = (delay = 0) => ({
    hidden: {opacity: 0, y: 40},
    visible: {opacity: 1, y: 0, transition: {duration: 0.6, ease: 'easeOut', delay}},
});


function SectionLink({children, to}) {
    const inner = (
        <>
            <span>{children}</span>
            <img src={IMAGES.arrowRight} alt="" className={styles.sectionLinkArrow}/>
        </>
    );
    return to
        ? <Link to={to} className={styles.sectionLink} onClick={() => window.scrollTo(0, 0)}>{inner}</Link>
        : <div className={styles.sectionLink}>{inner}</div>;
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Landing() {
    const [slide, setSlide] = useState(0);
    const [paused, setPaused] = useState(false);
    const [impactTab, setImpactTab] = useState('portfolio');

    const advance = useCallback(() => {
        setSlide(prev => (prev + 1) % HERO_SLIDES.length);
    }, []);

    useEffect(() => {
        if (paused) return;
        const t = setTimeout(advance, SLIDE_DURATION);
        return () => clearTimeout(t);
    }, [slide, paused, advance]);

    return (
        <div className={styles.landing}>

            {/* ── HERO ─────────────────────────────────────────────────────────── */}
            <section
                className={styles.hero}
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
            >

                <AnimatePresence initial={false}>
                    <motion.div
                        key={slide}
                        className={styles.heroBg}
                        aria-hidden="true"
                        variants={slideVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{duration: 0.8, ease: 'easeInOut'}}
                    >
                        <LazyImage src={HERO_SLIDES[slide].image} alt="" priority={slide === 0}/>
                        <div className={styles.heroGradient}/>
                    </motion.div>
                </AnimatePresence>

                <Navbar theme="dark" cta="Request Consultancy" ctaTo="/contact#consultancy_form"/>

                {/* Slide content — text is static; only background images switch */}
                <div className={styles.heroContent}>
                    <div className={styles.heroLeft}>
                        <motion.h1
                            className={styles.heroTitle}
                            initial={{opacity: 0, y: 24}}
                            animate={{opacity: 1, y: 0}}
                            transition={{duration: 0.5, ease: 'easeOut'}}
                        >
                            {HERO_SLIDES[0].title}
                        </motion.h1>
                        <motion.div
                            className={styles.heroTagline}
                            initial={{opacity: 0, y: 16}}
                            animate={{opacity: 1, y: 0}}
                            transition={{duration: 0.5, ease: 'easeOut', delay: 0.08}}
                        >
                            <span className={styles.heroWelcomeText}>{HERO_SLIDES[0].taglinePrefix}</span>
                            <span className={styles.heroTektronText}>{HERO_SLIDES[0].taglineBrand}</span>
                            {HERO_SLIDES[0].taglineSuffix && (
                                <span className={styles.heroPlusText}>{HERO_SLIDES[0].taglineSuffix}</span>
                            )}
                        </motion.div>
                    </div>
                    <div className={styles.heroRight}>
                        <motion.p
                            className={styles.heroDesc}
                            initial={{opacity: 0, y: 20}}
                            animate={{opacity: 1, y: 0}}
                            transition={{duration: 0.5, ease: 'easeOut', delay: 0.12}}
                        >
                            {HERO_SLIDES[0].desc}
                        </motion.p>
                        <Link to="/contact#consultancy_form" className={styles.ctaBtn}>Request Consultancy</Link>
                    </div>
                </div>

                {/* Progress indicator lines */}
                <div className={styles.heroDots} role="tablist" aria-label="Slide indicators">
                    {HERO_SLIDES.map((_, i) => (
                        <button
                            key={i}
                            role="tab"
                            className={`${styles.heroDot} ${i === slide ? styles.heroDotActive : ''}`}
                            onClick={() => {
                                setSlide(i);
                                setPaused(false);
                            }}
                            aria-label={`Go to slide ${i + 1}`}
                            aria-selected={i === slide}
                        />
                    ))}
                </div>
            </section>

            {/* ── WHO WE ARE ───────────────────────────────────────────────────── */}
            <section className={styles.whoWeAre}>
                <motion.div
                    className={styles.wwaTop}
                    variants={fadeUp(0)}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{once: true, amount: 0.3}}
                >
                    <div className={styles.wwaHeaders}>
                        <span className={styles.labelGray}>WHO WE ARE</span>
                        <span className={styles.titleRed}>Our Story</span>
                    </div>
                    <div className={styles.wwaStory}>
                        <h2 className={styles.wwaStoryTitle}>
                            Engineering Excellence with Economic Purpose.
                        </h2>
                        <p className={styles.wwaStoryBody}>
                            TektronPlus Engineering is an engineering construction company
                            that offers a variety of services which includes road construction /rehabilitation
                            /maintenance, road furniture: road
                            marking, road signs, traffic safety devices, traffic management
                            control devices, renewable energy products and production of
                            thermoplastic road marking paint.
                        </p>
                    </div>
                </motion.div>

                <motion.div
                    className={styles.wwaCards}
                    variants={fadeUp(0.1)}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{once: true, amount: 0.2}}
                >
                    <div className={styles.wwaCard}>
                        <div className={styles.wwaCardImg}>
                            <LazyImage
                                src={IMAGES.missionImg}
                                srcSet={`${IMAGES.missionImg} 1160w, ${IMAGES.missionImgMd} 1302w`}
                                sizes="(max-width: 768px) 651px, 580px"
                                alt="Our Mission"
                            />
                        </div>
                        <h3 className={styles.wwaCardTitle}>Our Mission</h3>
                        <p className={styles.wwaCardBody}>
                            It is our mission to provide the finest engineering service
                            solutions for new constructions as well as existing structures.
                            Through careful analysis, attentive customer support, and cost
                            effective solutions. TektronPlus has proven itself tons has become a force to reckon with
                            whilst serving the Nigerian Public
                        </p>
                    </div>
                    <div className={styles.wwaCard}>
                        <div className={styles.wwaCardImg}>
                            <LazyImage
                                src={IMAGES.visionImg}
                                srcSet={`${IMAGES.visionImg} 1160w, ${IMAGES.visionImgMd} 1302w`}
                                sizes="(max-width: 768px) 651px, 580px"
                                alt="Our Vision"
                            />
                        </div>
                        <h3 className={styles.wwaCardTitle}>Our Vision</h3>
                        <p className={styles.wwaCardBody}>
                            To be the foremost catalyst for sustainable infrastructure,
                            building a future where engineering excellence and environmental
                            value exist in perfect alignment. We strive to set the global
                            benchmark for "Green" urban projects, transforming how the world
                            builds, moves, and powers itself.
                        </p>
                    </div>
                </motion.div>

                <motion.div
                    className={styles.videoSection}
                    variants={fadeUp(0)}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{once: true, amount: 0.2}}
                >
                    <div className={styles.videoWrapper}>
                        <video
                            className={styles.videoPlayer}
                            src="https://res.cloudinary.com/dhjzovpof/video/upload/v1775836604/KUGBO_BUS_TERMINAL_e7zkyh.mp4"
                            controls
                            playsInline
                        />
                    </div>
                    <p className={styles.videoCaption}>
                        <span className={styles.captionRed}>Precision in motion:</span>{' '}
                        <span className={styles.captionGray}>
              From thermoplastic road marking to solar hybrid installation.
            </span>
                    </p>
                </motion.div>

                <SectionLink to="/services">See all we offer</SectionLink>
            </section>

            {/* ── PARTNERS ─────────────────────────────────────────────────────── */}
            <section className={styles.partners}>
                <div className={styles.partnersInner}>
                    <motion.div
                        className={styles.partnersHeader}
                        variants={fadeUp(0)}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{once: true, amount: 0.3}}
                    >
                        <div className={styles.partnersHeaderLeft}>
              <span className={`${styles.titleRed} ${styles.partnersTitleRed} ${styles.partnerBrand}`}>
                BRANDS THAT TRUST OUR VISION
              </span>
                        </div>
                    </motion.div>
                </div>

                {/* Full-width scrolling ticker strip */}
                <div className={styles.partnerStrip} aria-hidden="true">
                    <div className={styles.marqueeTrack}>
                        {[...PARTNERS, ...PARTNERS].map((src, i) => (
                            <div key={i} className={styles.marqueeItem}>
                                <img src={src} alt=""/>
                            </div>
                        ))}
                    </div>
                    <div className={styles.stripFadeLeft}/>
                    <div className={styles.stripFadeRight}/>
                </div>
            </section>

            {/* ── SERVICES ─────────────────────────────────────────────────────── */}
            <section className={styles.services}>
                <div className={styles.servicesInner}>
                    <motion.div
                        className={styles.servicesHeader}
                        variants={fadeUp(0)}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{once: true, amount: 0.3}}
                    >
                        <div className={styles.servicesHeaderLeft}>
                            <span className={styles.labelGray}>OUR SERVICES</span>
                            <span className={styles.titleRed}>What we do</span>
                        </div>
                        <p className={styles.servicesDesc}>
                            TektronPlus offers engineering services and environmentally conscious
                            energy solutions related to new and existing structures.
                        </p>
                    </motion.div>

                    <div className={styles.servicesList}>
                        {/* Service 1 – text | image */}
                        <motion.div
                            className={`${styles.serviceCard} ${styles.serviceCardNormal}`}
                            variants={fadeUp(0)}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{once: true, amount: 0.2}}
                        >
                            <div className={styles.serviceTextSection}>
                                <div className={styles.serviceIconBadge}>
                                    <img src={IMAGES.serviceIcon1} alt="" className={styles.serviceIconImg}/>
                                </div>
                                <div className={styles.serviceText}>

                                    <h3 className={styles.serviceTitle}>
                                        Road Construction &amp; Infrastructure Development/Maintenance
                                    </h3>
                                    <p className={styles.serviceBody}>
                                        Our experience is not limited to asphalt paving, concrete concrete
                                        paving and the aggregate sub base. We also perform a majority of the
                                        associated works such as: water mains, sewers, culvert repairs, earthworks,
                                        concrete sidewalks and curb/ gutter formation.
                                    </p>
                                </div>
                            </div>

                            <div className={styles.serviceImg}>
                                <LazyImage src={IMAGES.service1} alt="Road Construction"/>
                            </div>
                        </motion.div>

                        {/* Service 2 – image | text (row-reverse: text first in DOM → appears RIGHT) */}
                        <motion.div
                            className={`${styles.serviceCard} ${styles.serviceCardReversed}`}
                            variants={fadeUp(0)}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{once: true, amount: 0.2}}
                        >
                            <div className={`${styles.serviceTextSection}`}>
                                <div className={`${styles.serviceIconBadge}`}>
                                    <img src={IMAGES.serviceIcon2} alt="" className={styles.serviceIconImg}/>
                                </div>

                                <div className={styles.serviceText}>
                                    <h3 className={styles.serviceTitle}>
                                        Production Of Thermoplastic &amp; Line Making Materials.
                                    </h3>
                                    <p className={styles.serviceBody}>
                                        TektronThermo is committed to a highly specialized production and retail
                                        of thermoplastic lane marking paint. Our special formulations are
                                        designed to cater to our clients needs This is to maintain quality control on
                                        all our projects and also to provide other contractors/ retailers the highest
                                        quality thermoplastic paint.
                                    </p>
                                </div>
                            </div>

                            <div className={`${styles.serviceImg}  ${styles.serviceImgCover}`}>
                                <LazyImage src={IMAGES.service2} alt="Thermoplastic Production"/>
                            </div>
                        </motion.div>

                        {/* Service 3 – text | image */}
                        <motion.div
                            className={`${styles.serviceCard} ${styles.serviceCardNormal}`}
                            variants={fadeUp(0)}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{once: true, amount: 0.2}}
                        >
                            <div className={styles.serviceTextSection}>
                                <div className={styles.serviceIconBadge}>
                                    <img src={IMAGES.serviceIcon3} alt="" className={styles.serviceIconImg}/>
                                </div>
                                <div className={styles.serviceText}>
                                    <h3 className={styles.serviceTitle}>
                                        Traffic Management &amp; Road Safety Solutions
                                    </h3>
                                    <p className={styles.serviceBody}>
                                        We provide all inclusive Road Furniture which includes- Provision and
                                        installation of Solar Lights/ CCTV, Road Signs, Highway Signs, Traffic lights,
                                        Speed bumps, Reflective Cat Eye Studs and Traffic Data Counter
                                        Classifiers.
                                    </p>
                                </div>
                            </div>

                            <div className={`${styles.serviceImg} ${styles.traffic}`}>
                                <LazyImage src={IMAGES.service3} className={styles.trafficAdjust}
                                           alt="Traffic Management"/>
                            </div>
                        </motion.div>
                    </div>

                    <SectionLink to="/services">See all we offer</SectionLink>
                </div>
            </section>

            {/* ── OUR IMPACT / PORTFOLIO ───────────────────────────────────────── */}
            <section className={styles.portfolio}>
                <div className={styles.portfolioInner}>
                    <motion.div
                        className={styles.portfolioHeader}
                        variants={fadeUp(0)}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{once: true, amount: 0.3}}
                    >
                        <div className={styles.portfolioHeaderLeft}>
                            <span className={styles.labelGray} style={{fontSize: '20px'}}>OUR IMPACT</span>
                            <div className={styles.portfolioTitles}>
                                <button
                                    className={impactTab === 'portfolio' ? styles.titleRed : styles.portfolioProjectsBtn}
                                    onClick={() => setImpactTab('portfolio')}
                                >
                                    Portfolio
                                </button>
                                <button
                                    className={impactTab === 'projects' ? styles.titleRed : styles.portfolioProjectsBtn}
                                    onClick={() => setImpactTab('projects')}
                                >
                                    Projects
                                </button>
                            </div>
                        </div>
                        <p className={styles.portfolioDesc}>
                            Mapping a decade of infrastructure excellence across the federation.
                        </p>
                    </motion.div>

                    <AnimatePresence mode="wait">
                        {impactTab === 'portfolio' ? (
                            <motion.div
                                key="portfolio"
                                className={styles.portfolioMapWrap}
                                initial={{opacity: 0, y: 20}}
                                animate={{opacity: 1, y: 0}}
                                exit={{opacity: 0, y: -20}}
                                transition={{duration: 0.3}}
                            >
                                {/* Stat — top left */}
                                <div className={`${styles.stat} ${styles.statTopLeft}`}>
                                    <span className={styles.statNum}>40+</span>
                                    <span className={styles.statLabel}>Projects Delivered</span>
                                </div>

                                {/* Stat — top right */}
                                <div className={`${styles.stat} ${styles.statTopRight}`}>
                                    <span className={styles.statNum}>10+</span>
                                    <span className={styles.statLabel}>Years of Technical Mastery</span>
                                </div>

                                {/* Map */}
                                <div className={styles.mapContainer}>
                                    <NigeriaMap height="100%" className={styles.portfolioMap}/>
                                </div>

                                {/* Stat — bottom center */}
                                <div className={`${styles.stat} ${styles.statBottomCenter}`}>
                                    <span className={styles.statNum}>20+</span>
                                    <span className={styles.statLabel}>States Covered</span>
                                </div>

                                {/* Description — bottom left */}
                                <p className={styles.portfolioMapText}>
                                    From the reconstruction of vital arterial roads to the deployment of
                                    multi-megawatt solar hybrid systems, our portfolio represents a
                                    commitment to national resilience and economic purpose.
                                </p>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="projects"
                                className={styles.projectsGrid}
                                initial={{opacity: 0, y: 20}}
                                animate={{opacity: 1, y: 0}}
                                exit={{opacity: 0, y: -20}}
                                transition={{duration: 0.3}}
                            >
                                {PROJECTS.slice(0, 6).map((project, i) => (
                                    <motion.div
                                        key={project.id}
                                        // className={styles.projectCard}
                                        initial={{opacity: 0, y: 24}}
                                        animate={{opacity: 1, y: 0}}
                                        transition={{duration: 0.3, delay: i * 0.06}}
                                    >
                                        <ProjectCard project={project}/>
                                    </motion.div>
                                ))}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <SectionLink className={styles.navLink} to="/impact">Explore The Full Archive</SectionLink>
                </div>
            </section>

            {/* ── SUSTAINABILITY ────────────────────────────────────────────────── */}
            <section className={styles.sustainability}>
                <div className={styles.sustainabilityLeft}>
                    <h2 className={styles.sustainabilityTitle}>SUSTAINABILITY</h2>
                    <div className={styles.pillars}>
                        <div className={styles.pillar}>
                            <div className={styles.pillarIcon}>
                                <img src={IMAGES.sunIcon} alt=""/>
                            </div>
                            <p className={styles.pillarText}>
                                Integrating solar hybrid systems to power civil infrastructure.
                            </p>
                        </div>
                        <div className={styles.pillar}>
                            <div className={styles.pillarIcon}>
                                <img src={IMAGES.roadIcon} alt=""/>
                            </div>
                            <p className={styles.pillarText}>
                                Using high-grade thermoplastic and aggregates for longevity.
                            </p>
                        </div>
                        <div className={styles.pillar}>
                            <div className={styles.pillarIcon}>
                                <img src={IMAGES.shieldIcon} alt=""/>
                            </div>
                            <p className={styles.pillarText}>
                                Optimizing project lifecycles to reduce long-term maintenance costs.
                            </p>
                        </div>
                    </div>
                </div>
                <div className={styles.plusFactor}>
                    <div className={styles.plusFactorContent}>
                        <span className={styles.plusFactorLabel}>THE PLUS FACTOR</span>
                        <div className={styles.plusFactorTexts}>
                            <h3 className={styles.plusFactorTitle}>
                                Engineering for a Resilient Future.
                            </h3>
                            <p className={styles.plusFactorBody}>
                                At Tektron+, sustainability is not an afterthought; it is our
                                blueprint. We integrate renewable energy solutions and durable
                                engineering practices into national infrastructure, ensuring our
                                projects serve both the economy and the environment for decades
                                to come.
                            </p>
                        </div>
                    </div>
                    <img
                        src={IMAGES.logoWatermark}
                        alt=""
                        className={styles.plusFactorWatermark}
                        aria-hidden="true"
                    />
                </div>
            </section>

            {/* ── CONTACT CTA ──────────────────────────────────────────────────── */}
            <ContactCTA/>

            {/* ── FOOTER ───────────────────────────────────────────────────────── */}
            <Footer/>

        </div>
    );
}
